import { Maybe } from '@codegrade/maybe';
import { array, itertools, object } from '@codegrade/utils';
import { colorToString } from './color';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class WrapperContent {
    content;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(content) {
        this.content = content;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class EmphasizedContent extends WrapperContent {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'EmphasizedContent';
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class BoldContent extends WrapperContent {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'BoldContent';
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class MonospaceContent extends WrapperContent {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'MonospaceContent';
}
// Force the content of this block to be rendered on a single line if possible
// and the document format supports it.
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class NonBreakingContent {
    content;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'NonBreakingContent';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(content) {
        this.content = content;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ContentBlock {
    chunks;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(chunks) {
        this.chunks = chunks;
    }
}
/**
 * Typesets a string as bold and returns the content chunk.
 *
 * @param str The text to be typeset as bold.
 * @returns The content chunk corresponding to the content.
 */
export function boldString(str) {
    return new BoldContent(new ContentBlock([str]));
}
/**
 * Creates a table cell representation.
 *
 * @param content The content of the cell.
 * @param background An eventual color to set as background.
 * @returns The table cell representation.
 */
export function tableCell(content, background) {
    return { content, background };
}
/** Represents a table in the document model. */
class TableN {
    rows;
    alignmentOptions;
    borderOptions;
    /**
     * @param rows Array of rows to be included in the table.
     * Each row must have the same number of elements as the table width.
     * @param alignmentOptions The alignment options for the table columns.
     * Must be of the same length as the table width.
     * @param borderOptions Whether to render inner and/or outer border within the table.
     */
    constructor(rows, alignmentOptions, borderOptions = { inner: false, outer: false }) {
        this.rows = rows;
        this.alignmentOptions = alignmentOptions;
        this.borderOptions = borderOptions;
    }
}
/** Representation of a 2 column table. */
export class Table2 extends TableN {
    /** @inheritdoc */
    nCols = 2;
    /** @inheritdoc */
    addRows(...rows) {
        return new Table2([...this.rows, ...rows], this.alignmentOptions, this.borderOptions);
    }
}
/** Representation of a 5 column table. */
export class Table5 extends TableN {
    /** @inheritdoc */
    nCols = 5;
    /** @inheritdoc */
    addRows(...rows) {
        return new Table5([...this.rows, ...rows], this.alignmentOptions, this.borderOptions);
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ColumnLayout {
    columns;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'ColumnLayout';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(columns) {
        this.columns = columns;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class _SubSection {
    heading;
    children;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = '_SubSection';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(heading, children) {
        this.heading = heading;
        this.children = children;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class SubSection extends _SubSection {
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class ColumnSubSection extends _SubSection {
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class Section {
    heading;
    children;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'Section';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(heading, children) {
        this.heading = heading;
        this.children = children;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class NewPage {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    brand = 'NewPage';
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class DocumentRoot {
    children;
    header;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(children, header) {
        this.children = children;
        this.header = header;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    addChildren(children) {
        return new DocumentRoot([...this.children, ...children], this.header);
    }
    /**
     * Adds a header to the document.
     *
     * @param header The header to be added.
     * @returns A new DocumentRoot with the header.
     */
    setHeader(header) {
        return new DocumentRoot(this.children, Maybe.of(header));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static makeEmpty() {
        return new DocumentRoot([], Maybe.empty());
    }
}
/** Class representing a general document rendering engine. */
class _DocumentBackend {
    doc;
    /** The name of the backend, can be used to display for the user. */
    static backendName;
    /** The file extension that files generated by this backend should use. */
    static fileExtension;
    /** The MIME type of the files produced by this backend. */
    static mimeType;
    /**
     * Class constructor.
     *
     * @param doc The document to render.
     */
    constructor(doc) {
        this.doc = doc;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class LatexDocument extends _DocumentBackend {
    doc;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static backendName = 'LaTeX';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fileExtension = 'tex';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static mimeType = 'text/x-tex';
    /* eslint-disable-next-line prefer-regex-literals, jsdoc/require-jsdoc -- When touching please add documentation. */
    static endListingRegex = new RegExp('\\\\end{lstlisting}', 'g');
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static reUnescapedLatex = /[{}\\#$%&^_~]/g;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static reHasUnescapedLatex = RegExp(LatexDocument.reUnescapedLatex.source);
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static latexEscapes = Object.freeze({
        '{': '\\{',
        '}': '\\}',
        '\\': '\\textbackslash{}',
        '#': '\\#',
        $: '\\$',
        '%': '\\%',
        '&': '\\&',
        '^': '\\textasciicircum{}',
        _: '\\_',
        '~': '\\textasciitilde{}',
    });
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static escape(input) {
        if (input !== '' && LatexDocument.reHasUnescapedLatex.test(input)) {
            const map = LatexDocument.latexEscapes;
            return input.replace(LatexDocument.reUnescapedLatex, ent => map[ent]);
        }
        return input;
    }
    /**
     * Creates a new instance of the document backend asynchronously.
     *
     * @param doc The document representation to be rendered with the current engine.
     * @returns A new instance of the Document Backend for this format.
     */
    static make(doc) {
        return Promise.resolve(new LatexDocument(doc));
    }
    /**
     * @param doc The document representation to be rendered with the current engine.
     * @returns A new instance of the Document Backend for this format.
     */
    constructor(doc) {
        super(doc);
        this.doc = doc;
    }
    /**
     * Renders lines of LaTeX script for elements of the document representation.
     *
     * @param el The element of the document representation to be rendered.
     * @returns The rendered element.
     */
    renderLines(el) {
        if (el instanceof Section) {
            return this.renderSection(el);
        }
        else if (el instanceof SubSection) {
            return this.renderSubsection(el);
        }
        else if (el instanceof ColumnLayout) {
            return this.renderColumn(el);
        }
        else if (el instanceof ContentBlock) {
            return this.renderContentBlock(el);
        }
        else if (el instanceof TableN) {
            return this.renderTable(el);
        }
        else if (el instanceof NewPage) {
            return [['\\clearpage{}'], []];
        }
        else {
            return this.renderCode(el);
        }
    }
    /** @returns The rendered header lines. */
    renderHeader() {
        /**
         * Renders a single header line.
         *
         * @param el The content of one header line.
         * @returns The rendered line for the header.
         */
        const mapHeaderLinesToContent = (el) => this.renderLines(new ContentBlock([el]));
        return this.doc.header.mapOrDefault(header => {
            const [linesLeft, highlightsLeft] = array.unzip2(header.left.map(mapHeaderLinesToContent));
            const [linesRight, highlightsRight] = array.unzip2(header.right.map(mapHeaderLinesToContent));
            const newLine = '\\linebreak\n';
            return [
                [
                    `\\fancyhead{} % clear all header fields

        \\fancyhead[L]{
${array.flat1(linesLeft).join(newLine)}}

\\fancyhead[R]{
${array.flat1(linesRight).join(newLine)}}`,
                ],
                array.flat1([...highlightsLeft, ...highlightsRight]),
            ];
        }, [[], []]);
    }
    /** @inheritdoc */
    renderToBuffer() {
        const [lines, highlights] = array.unzip2(this.doc.children.map(child => this.renderLines(child)));
        const [headerLines, headerHiglights] = this.renderHeader();
        const base = `\\documentclass{article}
\\usepackage{listings}
\\usepackage[table,xcdraw]{xcolor}
\\usepackage{fontspec}
\\usepackage{textcomp}
\\usepackage{paracol}
\\usepackage[margin=0.5in, top=1.25in, headsep=0.75in]{geometry}
\\usepackage{fancyhdr}
\\usepackage{lastpage}
\\usepackage{tabulary}

\\setmainfont{Noto Serif}
\\setmonofont{Noto Sans Mono}

\\definecolor{bluekeywords}{rgb}{0.13, 0.13, 1}
\\definecolor{greencomments}{rgb}{0, 0.5, 0}
\\definecolor{redstrings}{rgb}{0.9, 0, 0}
\\definecolor{graynumbers}{rgb}{0.5, 0.5, 0.5}

${this.defineColors(array.flat1(highlights).concat(headerHiglights)).join('\n')}

\\pagestyle{fancy}

${headerLines.join('')}

\\fancyfoot{} % clear all footer fields
\\fancyfoot[C]{page \\thepage\\ of \\pageref*{LastPage}}

% XXX: This may break when the package "lstlinebgrd" is updated. If compilation
% of this document fails with the error "Error: Numbers none unknown", try to
% delete everything from "START fix" to "END fix", and uncomment the line below
% this one.
%\\usepackage{lstlinebgrd}
% START Fix for incompatibility between newer versions of listings and
% lstlinebgrd
\\makeatletter
\\let\\old@lstKV@SwitchCases\\lstKV@SwitchCases
\\def\\lstKV@SwitchCases#1#2#3{}
\\makeatother
\\usepackage{lstlinebgrd}
\\makeatletter
\\let\\lstKV@SwitchCases\\old@lstKV@SwitchCases

\\lst@Key{numbers}{none}{%
    \\def\\lst@PlaceNumber{\\lst@linebgrd}%
    \\lstKV@SwitchCases{#1}%
    {none:\\\\%
     left:\\def\\lst@PlaceNumber{\\llap{\\normalfont
                \\lst@numberstyle{\\thelstnumber}\\kern\\lst@numbersep}\\lst@linebgrd}\\\\%
     right:\\def\\lst@PlaceNumber{\\rlap{\\normalfont
                \\kern\\linewidth \\kern\\lst@numbersep
                \\lst@numberstyle{\\thelstnumber}}\\lst@linebgrd}%
    }{\\PackageError{Listings}{Numbers #1 unknown}\\@ehc}}
\\makeatother
% END fix


\\lstset{
    numbers=left,
    columns=fullflexible,
    showspaces=false,
    showtabs=false,
    breaklines=true,
    showstringspaces=false,
    breakatwhitespace=false,
    commentstyle=\\color{greencomments},
    keywordstyle=\\color{bluekeywords},
    stringstyle=\\color{redstrings},
    numberstyle=\\color{graynumbers},
    basicstyle=\\ttfamily\\footnotesize,
    xleftmargin=12pt,
    rulesepcolor=\\color{graynumbers},
    tabsize=4,
    captionpos=t,
    frame=L,
    upquote=true
}

\\begin{document}
${array.flat1(lines).join('\n')}
\\end{document}`;
        return Promise.resolve(new TextEncoder().encode(base));
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    defineColors(highlights) {
        return [...itertools.unique(h => h.id, highlights)].reduce((acc, highlight) => {
            const id = highlight.id;
            const bg = highlight.background;
            const fg = highlight.foreground;
            acc.push(`\\definecolor{bg-color-${id}}{RGB}{${bg.red}, ${bg.green}, ${bg.blue}}`, `\\definecolor{fg-color-${id}}{RGB}{${fg.red}, ${fg.green}, ${fg.blue}}`);
            return acc;
        }, ['% Unfortunately the foreground colors are not used.']);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderContentBlock(contentBlock) {
        const [content, highlights] = contentBlock.chunks.reduce((acc, chunk) => {
            if (chunk instanceof NonBreakingContent) {
                const b = chunk.content.join('~');
                acc[0].push(LatexDocument.escape(b).replace(/[ \t]+/g, '~'));
                return acc;
            }
            else if (chunk instanceof WrapperContent) {
                const toWrap = this.renderContentBlock(chunk.content);
                acc[0].push(`\\${LatexDocument.wrapperTag(chunk)}{`);
                acc[0].push(toWrap[0][0]);
                acc[0].push('}');
                return [acc[0], acc[1].concat(toWrap[1])];
            }
            else {
                acc[0].push(LatexDocument.escape(chunk));
                return acc;
            }
        }, [[], []]);
        return [[content.join('')], highlights];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static wrapperTag(chunk) {
        if (chunk instanceof EmphasizedContent) {
            return 'emph';
        }
        else if (chunk instanceof BoldContent) {
            return 'textbf';
        }
        else if (chunk instanceof MonospaceContent) {
            return 'texttt';
        }
        else {
            throw new Error('Unsupported wrapper.');
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderSection(section) {
        return section.children.reduce((accum, child) => {
            const res = this.renderLines(child);
            return [accum[0].concat(res[0]), accum[1].concat(res[1])];
        }, [[`\\section{${LatexDocument.escape(section.heading)}}`], []]);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderSubsection(subsection) {
        return subsection.children.reduce((accum, child) => {
            const res = this.renderLines(child);
            return [accum[0].concat(res[0]), accum[1].concat(res[1])];
        }, [[`\\subsection{${LatexDocument.escape(subsection.heading)}}`], []]);
    }
    /**
     * Renders a table cell.
     *
     * @param cell A table cell.
     * @returns The rendered content block for the cell.
     */
    renderCell(cell) {
        const [[renderedContent], highlights] = this.renderContentBlock(new ContentBlock([cell.content]));
        if (cell.background != null) {
            return [
                [`\\cellcolor[HTML]{${colorToString(cell.background)}}${renderedContent}`],
                highlights,
            ];
        }
        else {
            return [[renderedContent], highlights];
        }
    }
    /**
     * Renders the table representation into latex code.
     *
     * @param table A table representation to be rendered.
     * @returns The rendered representation with highlights.
     */
    renderTable(table) {
        const outer = table.borderOptions.outer ? '|' : '';
        const inner = table.borderOptions.inner ? '|' : '';
        const alignmentPattern = `${outer}${table.alignmentOptions.join(inner)}${outer}`;
        return [
            [
                `
\\begin{table}[h!]
\\raggedright
\\begin{tabulary}{\\linewidth}{${alignmentPattern}}
${table.borderOptions.outer ? '\\hline \n' : ''}
${table.rows
                    .map(row => row.map(cell => this.renderCell(cell)[0].join('')).join('&'))
                    .join(table.borderOptions.inner ? '\\\\ \\hline \n' : '\\\\ \n')}
    ${table.borderOptions.outer ? '\\hline \n' : ''}
\\end{tabulary}
\\end{table}
        `,
            ],
            [],
        ];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderCode(code) {
        // TODO: don't render caption when none given.
        let caption = '';
        let highlights = [];
        if (code.caption != null) {
            [[caption], highlights] = this.renderContentBlock(code.caption);
        }
        return [
            [
                '\\begin{lstlisting}[',
                `    firstnumber=${code.firstLine},`,
                `    linebackgroundcolor=${this.makeHighlights(code.highlights)},`,
                `    caption = {\\raggedright ${caption}}]`,
                ...code.lines.map(line => line.replace(LatexDocument.endListingRegex, '\\end {lstlisting}')),
                '\\end{lstlisting}',
            ],
            highlights.concat(code.highlights.map(range => range.highlight)),
        ];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    makeHighlights(ranges) {
        const sorted = ranges.sort((a, b) => a.end - b.end);
        return this.makeHighlightsSorted(sorted);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    makeHighlightsSorted(ranges) {
        // Ranges must be sorted and not overlap. Because there is no "and"
        // operator in TeX that is the only way we can guarantee that the
        // nested if-else below will work correctly.
        // TODO: Find a way to change the foreground color of specific lines.
        // Maybe simply wrapping each line in a {\color{fg-color-${id}} ...}
        // will work.
        const [cur, ...rest] = ranges;
        if (rest.length === 0) {
            return `
\\ifnum\\value{lstnumber}<${cur.end + 1}%
    \\ifnum\\value{lstnumber}>${cur.start - 1}%
        \\color{bg-color-${cur.highlight.id}}%
    \\fi%
\\fi
            `.trim();
        }
        else {
            return `
\\ifnum\\value{lstnumber}<${cur.end + 1}%
    \\ifnum\\value{lstnumber}>${cur.start - 1}%
        \\color{bg-color-${cur.highlight.id}}%
    \\fi%
\\else%
    ${this.makeHighlightsSorted(rest)}%
\\fi
            `.trim();
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderColumn(column) {
        const columns = column.columns.length;
        return column.columns.reduce((acc, block, index) => {
            const res = this.renderLines(block);
            acc[0] = acc[0].concat(res[0]);
            if (index !== column.columns.length - 1) {
                acc[0].push('\\switchcolumn');
            }
            else {
                acc[0].push('\\end{paracol}');
            }
            acc[1] = acc[1].concat(res[1]);
            return acc;
        }, [[`\\begin{paracol}{${columns}}`], []]);
    }
}
/**
 * Renders a document as PDF.
 *
 * This first renders the document as LaTeX with the `LatexDocument` backend,
 * which then gets rendered as a PDF with our latexmk service.
 */
class PdfDocument extends _DocumentBackend {
    /** @inheritdoc */
    static backendName = 'PDF';
    /** @inheritdoc */
    static fileExtension = 'pdf';
    /** @inheritdoc */
    static mimeType = 'application/pdf';
    /**
     * Get an instance of this class.
     *
     * @param doc The document to render.
     * @returns A promise resolving with a new instance of this class.
     */
    static make(doc) {
        return Promise.resolve(new PdfDocument(doc));
    }
    /** @inheritdoc */
    async renderToBuffer() {
        const latexDoc = await LatexDocument.make(this.doc);
        const latexSrc = await latexDoc.renderToBuffer();
        return await fetch('/api/v2/latexmk', {
            method: 'POST',
            body: latexSrc,
            headers: {
                'Content-Type': 'application/x-tex',
                'Content-Length': `${latexSrc.byteLength}`,
            },
        }).then(response => {
            if (response.status === 200) {
                return response.arrayBuffer();
            }
            return response.text().then(msg => Promise.reject(new Error(msg)));
        });
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class DocxDocument extends _DocumentBackend {
    doc;
    docxType;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static backendName = 'Docx';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fileExtension = 'docx';
    /** The footer of the document. */
    footer;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    codeBlockIndex = 0;
    /**
     * Creates a new instance of the document backend asynchronously.
     *
     * @param doc The document representation to be rendered with the current engine.
     * @returns A new instance of the Document Backend for this format.
     */
    static async make(doc) {
        const docxType = await import('docx');
        return new DocxDocument(doc, docxType);
    }
    /** @returns The rendered document header. */
    renderHeader() {
        return new this.docxType.Header({
            children: this.doc.header.mapOrDefault(header => {
                const maxLength = Math.max(header.right.length, header.left.length);
                const zippedArray = array.zip(array.pad(header.left, maxLength, () => ''), array.pad(header.right, maxLength, () => ''));
                const headerTable = new Table2(zippedArray.map(r => [tableCell(r[0]), tableCell(r[1])]), ['l', 'r']);
                return this.renderTable(headerTable);
            }, []),
        });
    }
    /**
     * @param doc The document representation to be rendered with the current engine.
     * @param docxType The type definitions for the docx module.
     * @returns A new instance of the Document Backend for this format.
     */
    constructor(doc, docxType) {
        super(doc);
        this.doc = doc;
        this.docxType = docxType;
        this.footer = new docxType.Footer({
            children: [
                new this.docxType.Paragraph({
                    alignment: this.docxType.AlignmentType.CENTER,
                    children: [
                        new this.docxType.TextRun({
                            children: [
                                'page ',
                                this.docxType.PageNumber.CURRENT,
                                ' of ',
                                this.docxType.PageNumber.TOTAL_PAGES,
                            ],
                        }),
                    ],
                }),
            ],
        });
    }
    /** @inheritdoc */
    async renderToBuffer() {
        const doc = new this.docxType.Document({
            sections: this.doc.children.map(child => this.renderDocument(child)),
            numbering: {
                config: [...this.codeBlockNumberings()],
            },
        });
        return await this.docxType.Packer.toBlob(doc);
    }
    /**
     * Renders the document representation with the docx package.
     *
     * @param child A child element of the document representation.
     * @returns The rendered element and its children as a docx document representation.
     */
    renderDocument(child) {
        if (child instanceof Section) {
            return this.renderSection(child);
        }
        else {
            return {
                properties: {
                    type: this.docxType.SectionType.CONTINUOUS,
                },
                headers: {
                    default: this.renderHeader(),
                },
                footers: {
                    default: this.footer,
                },
                children: this.renderElement(child),
            };
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    codeBlockNumberings() {
        return this.getCodeBlocks().map((block, i) => ({
            levels: [
                {
                    level: 0,
                    format: this.docxType.LevelFormat.DECIMAL,
                    start: block.firstLine,
                    text: '%1',
                    alignment: this.docxType.AlignmentType.END,
                    style: {
                        paragraph: {
                            indent: { left: 720, hanging: 260 },
                        },
                    },
                },
            ],
            reference: `decimal-ordered-${i}`,
        }));
    }
    /** @returns An array of all the code blocks present within the document. */
    getCodeBlocks() {
        /**
         * Parses all the elements in the document representation and returns an
         * array of all the code blocks present within the document.
         *
         * @param els The elements from the document representation.
         * @returns The array of code blocks element.
         */
        function getBlocks(els) {
            return els.reduce((acc, el) => {
                if (el instanceof Section) {
                    return acc.concat(getBlocks(el.children));
                }
                else if (el instanceof SubSection) {
                    return acc.concat(getBlocks(el.children));
                }
                else if (el instanceof ColumnLayout) {
                    return acc.concat(getBlocks(el.columns));
                }
                else if (el instanceof ContentBlock ||
                    el instanceof NewPage ||
                    el instanceof TableN) {
                    return acc;
                }
                else {
                    return acc.concat(el);
                }
            }, []);
        }
        return getBlocks(this.doc.children);
    }
    /**
     * Renders a section element from the document representation and its children.
     *
     * @param el The section element to be rendered.
     * @returns The rendered section element and its children.
     */
    renderSection(el) {
        return {
            properties: {
                type: this.docxType.SectionType.CONTINUOUS,
            },
            margins: {
                left: 360,
                right: 360,
                top: 360,
                bottom: 0,
            },
            headers: {
                default: this.renderHeader(),
            },
            footers: {
                default: this.footer,
            },
            children: itertools.flatMap1(el.children, child => this.renderElement(child)),
        };
    }
    /**
     * Renders a single element of the document representation.
     *
     * @param el The element to be rendered.
     * @returns The rendered element.
     */
    renderElement(el) {
        if (el instanceof SubSection) {
            return this.renderSubSection(el);
        }
        else if (el instanceof ColumnLayout) {
            return this.renderColumn(el);
        }
        else if (el instanceof ContentBlock) {
            return [new this.docxType.Paragraph({ children: this.renderContentBlock(el) })];
        }
        else if (el instanceof TableN) {
            return this.renderTable(el);
        }
        else if (el instanceof NewPage) {
            return this.renderNewPage();
        }
        else {
            return this.renderCode(el);
        }
    }
    /**
     * Renders the table representation into docx structure.
     *
     * @param table A table representation to be rendered.
     * @returns The rendered representation with highlights.
     */
    renderTable(table) {
        /** @returns A border object representing an invisible border. */
        const invisibleBorder = {
            style: this.docxType.BorderStyle.NONE,
            color: '000000',
            size: 1,
        };
        /** @returns A border object representing a 1pt single line black border. */
        const visibleBorder = {
            style: this.docxType.BorderStyle.SINGLE,
            color: '000000',
            size: 1,
        };
        /**
         * Rendeers a single cell with the correct borders depending on its
         * position within the table.
         *
         * @param cell The cell to be rendered.
         * @param opts The options relative to the position of the cell within the table.
         * @returns The rendered table cell with the correct borders.
         */
        const renderCell = (cell, opts) => {
            /**
             * Returns the correct border object based on the conditions.
             *
             * @param condition The condition to visualize the border of the cell or not.
             * @returns The correct border object.
             */
            const getBorder = (condition) => condition ? visibleBorder : invisibleBorder;
            const borders = {
                /* Outer border are visualized at the top, left, right, and bottom if the option is
                 * selected, and the cell is one of the external ones.
                 *
                 * Internal borders are only visualized on the top and left if the option is
                 * selected and the cell is NOT in the first row or column, so to make the internal
                 * grid pattern.
                 */
                top: getBorder((table.borderOptions.outer && opts.isFirstRow) ||
                    (table.borderOptions.inner && !opts.isFirstRow)),
                bottom: getBorder(table.borderOptions.outer && opts.isLastRow),
                left: getBorder((table.borderOptions.outer && opts.isFirstCell) ||
                    (table.borderOptions.inner && !opts.isFirstCell)),
                right: getBorder(table.borderOptions.outer && opts.isLastCell),
            };
            return new this.docxType.TableCell({
                margins: {
                    top: 5,
                    left: 5,
                    right: 5,
                    bottom: 5,
                    marginUnitType: this.docxType.WidthType.DXA,
                },
                children: this.renderElement(new ContentBlock([cell.content])),
                borders,
                shading: cell.background != null ? { fill: colorToString(cell.background) } : undefined,
            });
        };
        /**
         * Renders a row of cells as a TableRow.
         *
         * @param row The row of cells to be rendered.
         * @param isFirst Whether it is the first row.
         * @param isLast Whether it is the last row.
         * @returns The rendered table row.
         */
        const renderRow = (row, isFirst = false, isLast = false) => new this.docxType.TableRow({
            children: array.mapCustom(row, cell => renderCell(cell, {
                isFirstCell: false,
                isFirstRow: isFirst,
                isLastRow: isLast,
                isLastCell: false,
            }), cell => renderCell(cell, {
                isFirstCell: true,
                isFirstRow: isFirst,
                isLastRow: isLast,
                isLastCell: false,
            }), cell => renderCell(cell, {
                isFirstCell: false,
                isFirstRow: isFirst,
                isLastRow: isLast,
                isLastCell: true,
            })),
        });
        return [
            new this.docxType.Table({
                width: {
                    size: 100,
                    type: this.docxType.WidthType.PERCENTAGE,
                },
                rows: array.mapCustom([...table.rows], row => renderRow(row, false, false), row => renderRow(row, true, false), row => renderRow(row, false, true)),
            }),
        ];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderSubSection(el) {
        return [
            new this.docxType.Paragraph({
                text: el.heading,
                heading: this.docxType.HeadingLevel.HEADING_3,
            }),
            ...itertools.flatMap1(el.children, child => this.renderElement(child)),
        ];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderColumn(el) {
        const width = 100 / el.columns.length;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const getBorder = () => ({
            style: this.docxType.BorderStyle.NONE,
            color: '000000',
            size: 1,
        });
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const getBorders = () => ({
            top: getBorder(),
            bottom: getBorder(),
            left: getBorder(),
            right: getBorder(),
        });
        const table = new this.docxType.Table({
            rows: [
                new this.docxType.TableRow({
                    children: el.columns.map(child => new this.docxType.TableCell({
                        children: this.renderElement(child),
                        width: {
                            size: width,
                            type: this.docxType.WidthType.PERCENTAGE,
                        },
                        borders: getBorders(),
                    })),
                }),
            ],
            borders: getBorders(),
            width: { size: 100, type: this.docxType.WidthType.PERCENTAGE },
            columnWidths: el.columns.map(() => width),
            layout: this.docxType.TableLayoutType.FIXED,
        });
        return [table];
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderContentBlock(el, opts = {}) {
        return itertools.flatMap1(el.chunks, chunk => this.renderContentChunk(chunk, opts));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    renderContentChunk(el, opts) {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        const childOpts = (key, value) => ({ ...opts, [key]: value });
        if (el instanceof EmphasizedContent) {
            return this.renderContentBlock(el.content, childOpts('italics', true));
        }
        else if (el instanceof BoldContent) {
            return this.renderContentBlock(el.content, childOpts('bold', true));
        }
        else if (el instanceof MonospaceContent) {
            return this.renderContentBlock(el.content, childOpts('font', { name: 'Courier new' }));
        }
        else if (el instanceof NonBreakingContent) {
            // TODO: Try to make this non-breaking.
            return el.content.map(str => new this.docxType.TextRun(childOpts('text', str)));
        }
        else {
            return [new this.docxType.TextRun(childOpts('text', el))];
        }
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    renderNewPage() {
        return [new this.docxType.Paragraph({ pageBreakBefore: true })];
    }
    /* eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation. */
    renderCode(el) {
        const shadings = {};
        const colors = {};
        el.highlights.forEach(hl => {
            for (let i = hl.start; i <= hl.end; i++) {
                shadings[i] = colorToString(hl.highlight.background);
                colors[i] = colorToString(hl.highlight.foreground);
            }
        });
        const start = el.firstLine;
        const blockIdx = this.codeBlockIndex++;
        const pars = el.lines.map((line, offset) => {
            const lnum = start + offset;
            return new this.docxType.Paragraph({
                numbering: {
                    reference: `decimal-ordered-${blockIdx}`,
                    level: 0,
                },
                children: [
                    new this.docxType.TextRun({
                        text: line,
                        size: 18, // This is 9 point
                        font: { name: 'Courier New' },
                        // This is the text color
                        color: colors[lnum] ?? '000000',
                        shading: {
                            type: this.docxType.ShadingType.SOLID,
                            // This is the background/highlight color.
                            color: shadings[lnum] ?? 'FFFFFF',
                        },
                    }),
                ],
            });
        });
        if (el.caption != null) {
            pars.unshift(new this.docxType.Paragraph({
                alignment: this.docxType.AlignmentType.LEFT,
                spacing: {
                    before: 200,
                    after: 200,
                },
                children: this.renderContentBlock(el.caption),
            }));
        }
        return pars;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const backends = {
    [PdfDocument.backendName]: PdfDocument,
    [LatexDocument.backendName]: LatexDocument,
    [DocxDocument.backendName]: DocxDocument,
};
/**
 * Renders the document in the specified format.
 *
 * @param backendName The name of the backend engine to render the document with.
 * @param document The document tree representation to be rendered.
 * @returns The Array buffer or blob containing the file data in the chosen format.
 */
export async function render(backendName, document) {
    if (!object.hasAttr(backends, backendName)) {
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        throw new Error(`Invalid backend: ${backendName} `);
    }
    const backend = await backends[backendName].make(document);
    return await backend.renderToBuffer();
}
